import * as React from "react";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";

const GREEN = "#3CE97C";
const NAVY_DEEP = "#0B1730";

/** Max free-text length per turn, matching the widget's open field. */
const ASK_MAX_LENGTH = 500;

interface ComposerProps {
    /** Focused by the retry branch, so "say what was missing" lands in a
     * field that is already ready for it. */
    inputRef?: React.Ref<HTMLInputElement>;
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    /** True while a turn is in flight; the whole control goes inert. */
    busy: boolean;
}

/**
 * The follow-up composer at the foot of the chat.
 *
 * Replaces the row of leftover prompt chips that used to sit here. Those were
 * the only way on from an answer, so the chat dead-ended the moment they ran
 * out — and read as a non sequitur the moment the conversation turned to
 * support. A composer is a way on from every answer, so both problems go with
 * them.
 */
export default function Composer({
    inputRef,
    value,
    onChange,
    onSubmit,
    busy,
}: ComposerProps): React.JSX.Element {
    const canSend = !busy && value.trim().length > 0;

    return (
        <Stack
            direction="row"
            sx={{
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1.5,
                flexShrink: 0,
                borderTop: "1px solid rgba(168,184,217,0.10)",
            }}
        >
            <InputBase
                fullWidth
                inputRef={inputRef}
                value={value}
                disabled={busy}
                placeholder="Ask a follow-up…"
                inputProps={{
                    maxLength: ASK_MAX_LENGTH,
                    "aria-label": "Ask a follow-up",
                }}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        onSubmit();
                    }
                }}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 46,
                    borderRadius: "24px",
                    px: 2,
                    color: "#fff",
                    fontSize: 14,
                    bgcolor: "rgba(60,233,124,0.06)",
                    border: "1.5px solid rgba(60,233,124,0.55)",
                    boxShadow: "0 0 0 3px rgba(60,233,124,0.07)",
                    opacity: busy ? 0.5 : 1,
                    transition:
                        "border-color 120ms ease, box-shadow 120ms ease, background 120ms ease, opacity 120ms ease",
                    "& input::placeholder": { color: "#9DB0D0", opacity: 1 },
                    "&.Mui-focused": {
                        borderColor: GREEN,
                        bgcolor: "rgba(60,233,124,0.1)",
                        boxShadow: "0 0 0 4px rgba(60,233,124,0.18)",
                    },
                }}
            />
            <Box
                component="button"
                type="button"
                aria-label="send"
                disabled={!canSend}
                onClick={onSubmit}
                sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: 999,
                    border: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: NAVY_DEEP,
                    bgcolor: canSend ? GREEN : "rgba(60,233,124,0.82)",
                    opacity: busy ? 0.5 : 1,
                    cursor: canSend ? "pointer" : "default",
                    transition: "background 120ms ease, opacity 120ms ease",
                }}
            >
                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
            </Box>
        </Stack>
    );
}
